/**
 * Chart — full-featured single-symbol chart panel.
 *
 * Now with 16 user-settable toggles in the Settings popover. Each toggle
 * actually controls render behaviour (no decorative checkboxes):
 *
 *  - Gridlines             — dashed price grid in the price pane
 *  - Logarithmic Y-axis    — re-projects every series on log scale
 *  - Candle width          — slider 0.5×–1.5×
 *  - Show Extended Hours   — shades pre-/post-market (06:00–09:30 + 14:30–17:00 WAT)
 *  - Show Alerts           — renders alert pins from the `alerts` prop
 *  - Show Option Positions — NGX has no options market: shows a hint
 *  - Show Working Orders   — toggles the limit-line overlays + side chips
 *  - Show Trade Price      — toggles the right-side last-trade tag
 *  - Chart High/Low        — dashed H + L lines with labels (visible-window range)
 *  - Show Earnings Icon    — `E` badges at `earningsDates` events
 *  - Show Earnings Preview — extra mini bar showing post-event drift
 *  - Show Dividends Icon   — `D` badges at `dividendsDates` events
 *  - Fit Studies in Y-axis — folds zero-centred oscillators into the price pane
 *  - Show Positions        — horizontal line at user's avg cost from `positionAvgPrice`
 *  - Show Volume Overlay   — volume painted as faint background bars in price pane
 *  - Show Crosshair        — hover crosshair + OHLC tooltip
 *  - Zoom on scroll        — mouse-wheel zoom (in/out around cursor x)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { BrokerClient } from '../api/brokerClient';
import { scoreStock, type Verdict } from '../lib/signals';
import { STUDY_REGISTRY, STUDY_CATEGORIES, STUDIES_BY_ID, type StudyId, type StudyResult } from '../lib/study-registry';
import {
  type Drawing, type DrawingKind, type Point,
  POINTS_REQUIRED, TOOL_LABELS, TOOL_GLYPHS, TOOL_ORDER,
  fibRetracePrices, fibExtendPrices, channelLines,
  localDrawingStore, newDrawingId,
} from '../lib/drawings';

export type ChartType = 'candle' | 'hollow' | 'bar' | 'line' | 'area' | 'heikin';
export type Timeframe = '1m' | '5m' | '15m' | '30m' | '1H' | '4H' | 'D' | 'W' | 'M';

export interface Candle { t: number; open: number; high: number; low: number; close: number; volume: number; }
export interface WorkingOrder {
  brokerOrderId: string; clOrdId: string; accountId: string;
  symbol?: string;
  side: 'BUY' | 'SELL'; type: string; quantity: number;
  limitPrice?: string; status: string; placedAt: string;
}
export interface AlertSpec { price: number; direction: 'above' | 'below'; label?: string; }

export interface ChartSettings {
  gridlines: boolean; logScale: boolean; candleWidthScale: number;
  extendedHours: boolean; alerts: boolean; optionPositions: boolean;
  workingOrders: boolean; tradePrice: boolean; chartHighLow: boolean;
  earningsIcon: boolean; earningsPreview: boolean; dividendsIcon: boolean;
  fitStudies: boolean; positions: boolean; volumeOverlay: boolean;
  crosshair: boolean; zoomOnScroll: boolean;
}
const DEFAULT_SETTINGS: ChartSettings = {
  gridlines: true, logScale: false, candleWidthScale: 1,
  extendedHours: false, alerts: true, optionPositions: false,
  workingOrders: true, tradePrice: true, chartHighLow: false,
  earningsIcon: true, earningsPreview: false, dividendsIcon: true,
  fitStudies: false, positions: true, volumeOverlay: false,
  crosshair: true, zoomOnScroll: true,
};
const DEFAULT_STUDIES: Set<StudyId> = new Set(['volume', 'bb', 'sma20']);

interface Props {
  symbol: string;
  symbolLongName?: string;
  price: number;
  pctChange: number;
  candles?: Candle[];
  workingOrders?: WorkingOrder[];
  alerts?: AlertSpec[];
  earningsDates?: number[];
  dividendsDates?: number[];
  positionAvgPrice?: number;
  positionQuantity?: number;
  brokerClient?: BrokerClient;
  brokerId?: string;
  height?: number;
  bid?: number;
  ask?: number;
  bidSize?: number;
  askSize?: number;
  onQuickTrade?: (side: 'BUY' | 'SELL', symbol: string, price: number) => void;
}

export function Chart({
  symbol, symbolLongName, price, pctChange, candles, workingOrders,
  alerts, earningsDates, dividendsDates, positionAvgPrice, positionQuantity,
  brokerClient, brokerId, height = 540,
  bid, ask, bidSize, askSize, onQuickTrade,
}: Props) {
  const [chartType, setChartType] = useState<ChartType>('candle');
  const [timeframe, setTimeframe] = useState<Timeframe>('D');
  const [studies, setStudies] = useState<Set<StudyId>>(new Set(DEFAULT_STUDIES));
  const [settings, setSettings] = useState<ChartSettings>(DEFAULT_SETTINGS);
  const [drawings, setDrawings] = useState<Drawing[]>([]);
  const [activeTool, setActiveTool] = useState<DrawingKind | undefined>();
  const [pendingPts, setPendingPts] = useState<Point[]>([]);
  const [popover, setPopover] = useState<'studies' | 'drawings' | 'settings' | 'type' | undefined>();
  const [hover, setHover] = useState<{ i: number; x: number; y: number } | undefined>();
  const [setName, setSetName] = useState<string>('Default');
  const [allSets, setAllSets] = useState<string[]>(['Default']);
  const [zoom, setZoom] = useState(1);
  const svgRef = useRef<SVGSVGElement | null>(null);

  // ---- drawings persistence ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sets = await localDrawingStore.list(symbol);
      if (cancelled) return;
      setAllSets(sets);
      const initial = sets.includes('Default') ? 'Default' : (sets[0] ?? 'Default');
      setSetName(initial);
      const d = await localDrawingStore.load(symbol, initial);
      if (!cancelled) setDrawings(d);
    })();
    return () => { cancelled = true; };
  }, [symbol]);
  useEffect(() => { void localDrawingStore.save(symbol, setName, drawings); }, [symbol, setName, drawings]);

  async function pickSet(name: string) {
    setSetName(name);
    setDrawings(await localDrawingStore.load(symbol, name));
  }
  async function newSet() {
    const name = (typeof prompt !== 'undefined') ? prompt('New drawing set name:') : null;
    if (!name) return;
    setSetName(name); setDrawings([]);
    setAllSets((prev) => prev.includes(name) ? prev : [...prev, name]);
    await localDrawingStore.save(symbol, name, []);
  }

  const data = useMemo(() => candles ?? generateCandles(symbol, timeframe, 60), [candles, symbol, timeframe]);
  const display = useMemo(() => (chartType === 'heikin' ? toHeikinAshi(data) : data), [chartType, data]);

  const activePrice = useMemo(() => [...studies].map((id) => STUDIES_BY_ID[id]).filter((s) => s.placement === 'price'), [studies]);
  const activeSub = useMemo(() => [...studies].map((id) => STUDIES_BY_ID[id]).filter((s) => s.placement === 'sub'), [studies]);
  // Setting "Fit Studies in Y-axis" inlines zero-centred oscillators (CCI, MACD, AO, etc.) into the price pane.
  const inlineSub = settings.fitStudies ? activeSub.filter((s) => {
    const r = s.compute(display.slice(0, 5));
    return r.zeroLine === true;
  }) : [];
  const realSubPanes = activeSub.filter((s) => !inlineSub.includes(s));
  const subPanesData = realSubPanes.map((s) => ({ def: s, result: s.compute(display) }));
  const inlineSubData = inlineSub.map((s) => ({ def: s, result: s.compute(display) }));

  // ---------- layout ----------
  const W = 1100;
  const stripH = 36;
  const subPaneCount = subPanesData.length;
  const subPaneH = subPaneCount > 0 ? Math.min(110, Math.max(60, (height - stripH - 80) / Math.max(2, subPaneCount + 1))) : 0;
  const pricePaneH = height - stripH - subPaneCount * subPaneH;
  const padL = 8, padR = 56, padT = 8;
  const innerW = W - padL - padR;
  // Apply user zoom to candle layout — visible-window narrows when zoom > 1.
  const cwRaw = (innerW / display.length) * zoom;
  const cwBody = Math.max(1.5, cwRaw * 0.7 * settings.candleWidthScale);

  const max = Math.max(...display.map((d) => d.high));
  const min = Math.min(...display.map((d) => d.low));
  const span = max - min || 1;
  const yPrice = (p: number) => {
    if (settings.logScale && p > 0 && min > 0) {
      const lo = Math.log(min), hi = Math.log(max);
      return padT + (hi - Math.log(p)) / (hi - lo) * (pricePaneH - padT * 2);
    }
    return padT + (max - p) / span * (pricePaneH - padT * 2);
  };
  const xAt = (i: number) => padL + i * cwRaw + cwBody / 2;

  function svgToDomain(px: number, py: number): Point {
    const i = Math.max(0, Math.min(display.length - 1, Math.round((px - padL) / cwRaw)));
    let priceVal: number;
    if (settings.logScale && min > 0) {
      const lo = Math.log(min), hi = Math.log(max);
      priceVal = Math.exp(hi - (py - padT) / (pricePaneH - padT * 2) * (hi - lo));
    } else {
      priceVal = max - (py - padT) / (pricePaneH - padT * 2) * span;
    }
    return { x: i, y: priceVal };
  }
  const domainToSvg = (p: Point) => ({ x: xAt(p.x), y: yPrice(p.y) });

  function onCanvasClick(e: React.MouseEvent<SVGSVGElement>) {
    if (!activeTool || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) * (W / rect.width);
    const py = (e.clientY - rect.top) * (height / rect.height);
    if (py > pricePaneH) return;
    const pt = svgToDomain(px, py);
    const need = POINTS_REQUIRED[activeTool];
    const next = [...pendingPts, pt];
    if (next.length >= need) {
      const newDrawing: Drawing = { id: newDrawingId(), kind: activeTool, pts: next };
      if (activeTool === 'text') {
        const label = (typeof prompt !== 'undefined') ? prompt('Text:', 'note') : 'note';
        if (label === null) { setPendingPts([]); setActiveTool(undefined); return; }
        newDrawing.label = label;
      }
      setDrawings((prev) => [...prev, newDrawing]);
      setPendingPts([]); setActiveTool(undefined);
    } else setPendingPts(next);
  }
  function onCanvasMove(e: React.MouseEvent<SVGSVGElement>) {
    if (!settings.crosshair || !svgRef.current) { setHover(undefined); return; }
    const rect = svgRef.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) * (W / rect.width);
    const py = (e.clientY - rect.top) * (height / rect.height);
    if (px < padL || px > W - padR || py < padT || py > pricePaneH) { setHover(undefined); return; }
    const i = Math.max(0, Math.min(display.length - 1, Math.round((px - padL) / cwRaw)));
    setHover({ i, x: px, y: py });
  }
  function onWheel(e: React.WheelEvent<SVGSVGElement>) {
    if (!settings.zoomOnScroll) return;
    e.preventDefault();
    const factor = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom((z) => Math.max(0.5, Math.min(3, z * factor)));
  }

  function toggleStudy(id: StudyId) {
    setStudies((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }
  function deleteDrawing(id: string) { setDrawings((prev) => prev.filter((d) => d.id !== id)); }
  function clearDrawings() { setDrawings([]); setPendingPts([]); }
  async function cancelOrder(o: WorkingOrder) {
    if (!brokerClient || !brokerId) return;
    try {
      await brokerClient.call('cancelOrder', brokerId, {
        accountId: o.accountId, clOrdId: o.clOrdId, brokerOrderId: o.brokerOrderId,
      });
    } catch (err) { console.warn('cancel failed', err); }
  }

  // Derived window-high / window-low for "Chart High/Low" toggle.
  const winHigh = max; const winLow = min;
  const showOrders = settings.workingOrders ? (workingOrders ?? []) : [];

  return (
    <div className="chart-panel" style={{ height }}>
      <div className="ch-head">
        <div className="ch-head-left">
          <span className="ch-sym">{symbol}</span>
          <span className="ch-name">{symbolLongName ?? ''}</span>
          <span className="ch-px">{price.toFixed(2)}</span>
          <span className={`ch-pct ${pctChange >= 0 ? 'up' : 'down'}`}>{(pctChange >= 0 ? '+' : '') + pctChange.toFixed(2)}%</span>
          <TrafficLights symbol={symbol} price={price} pctChange={pctChange} bid={bid} ask={ask} recentCloses={data.map((c) => c.close)} />
        </div>
        <div className="ch-head-right">
          <QuickTradeBar
            symbol={symbol}
            price={price}
            pctChange={pctChange}
            bid={bid}
            ask={ask}
            bidSize={bidSize}
            askSize={askSize}
            onQuickTrade={onQuickTrade}
          />
          <Pop label={`Studies (${studies.size})`} open={popover === 'studies'} onToggle={() => setPopover((p) => p === 'studies' ? undefined : 'studies')}>
            <StudiesPanel active={studies} onToggle={toggleStudy} />
          </Pop>
          <Pop label={`Drawings (${drawings.length})`} open={popover === 'drawings'} onToggle={() => setPopover((p) => p === 'drawings' ? undefined : 'drawings')}>
            <DrawingsPanel
              active={activeTool}
              onPick={(t) => { setActiveTool(t); setPendingPts([]); setPopover(undefined); }}
              onClear={clearDrawings} onClose={() => setPopover(undefined)}
              setName={setName} allSets={allSets} onPickSet={pickSet} onNewSet={newSet}
              count={drawings.length}
            />
          </Pop>
          <button className="ch-btn ghost" onClick={() => setPopover((p) => p === 'settings' ? undefined : 'settings')}>⚙ Settings</button>
          {popover === 'settings' && <SettingsPanel value={settings} onChange={setSettings} onClose={() => setPopover(undefined)} />}
        </div>
      </div>

      <div className="ch-tools">
        <div className="ch-type">
          <button className="ch-pill active accent" onClick={() => setPopover((p) => p === 'type' ? undefined : 'type')}>▤ {labelOfType(chartType)}</button>
          {popover === 'type' && (
            <div className="ch-popover" style={{ left: 0, right: 'auto' }}>
              {(['candle','hollow','bar','line','area','heikin'] as const).map((t) => (
                <button key={t} className="ch-popover-item" onClick={() => { setChartType(t); setPopover(undefined); }}>{labelOfType(t)} {chartType === t ? '✓' : ''}</button>
              ))}
            </div>
          )}
        </div>
        <div className="ch-tf">
          {(['1m','5m','15m','30m','1H','4H','D','W','M'] as const).map((tf) => (
            <button key={tf} className={`ch-pill ${timeframe === tf ? 'active' : ''}`} onClick={() => setTimeframe(tf)}>{tf}</button>
          ))}
        </div>
        {zoom !== 1 && <button className="ch-pill" onClick={() => setZoom(1)}>Reset zoom ({zoom.toFixed(2)}×)</button>}
        {activeTool && (
          <span className="ch-active-tool">
            Drawing: {TOOL_LABELS[activeTool]} · {pendingPts.length}/{POINTS_REQUIRED[activeTool]} pts
            <button className="ch-tool-cancel" onClick={() => { setActiveTool(undefined); setPendingPts([]); }}>Esc</button>
          </span>
        )}
      </div>

      <svg
        ref={svgRef}
        className="ch-svg"
        viewBox={`0 0 ${W} ${height}`}
        preserveAspectRatio="none"
        onClick={onCanvasClick}
        onMouseMove={onCanvasMove}
        onMouseLeave={() => setHover(undefined)}
        onWheel={onWheel}
        style={{ cursor: activeTool ? 'crosshair' : 'default' }}
      >
        {/* Alternating horizontal background bands: 10 rows, dark blue + grey */}
        {(() => {
          const BANDS = 10;
          const y0 = padT;
          const y1 = pricePaneH - padT;
          const bandH = (y1 - y0) / BANDS;
          const out: React.ReactNode[] = [];
          for (let i = 0; i < BANDS; i++) {
            // Even rows: deep navy blue. Odd rows: slate grey.
            const fill = i % 2 === 0 ? '#0c1f44' : '#2b3140';
            out.push(
              <rect
                key={`zebra-${i}`}
                x={padL}
                y={y0 + i * bandH}
                width={W - padL - padR}
                height={bandH}
                fill={fill}
              />,
            );
          }
          return out;
        })()}
        {/* extended-hours shading */}
        {settings.extendedHours && display.map((d, i) => {
          const hour = new Date(d.t).getUTCHours() + 1; // approx WAT
          const ext = (hour < 9) || (hour > 14);
          if (!ext) return null;
          return <rect key={`ext-${i}`} x={padL + i * cwRaw} y={padT} width={cwRaw} height={pricePaneH - padT * 2}
                       fill="rgba(160,180,210,.07)" />;
        })}
        {/* gridlines */}
        {settings.gridlines && (
          <g stroke="#1a2742" strokeWidth=".5" strokeDasharray="2 3">
            {[0.15, 0.32, 0.5, 0.68, 0.85].map((f, k) => {
              const y = padT + f * (pricePaneH - padT * 2);
              return <line key={k} x1={padL} x2={W - padR} y1={y} y2={y} />;
            })}
          </g>
        )}
        {/* y-axis labels */}
        {[0, 0.25, 0.5, 0.75, 1].map((f, k) => {
          const p = max - (max - min) * f;
          const y = yPrice(p);
          return <text key={k} x={W - padR + 6} y={y + 3} fontSize="10" fill="#7a8cab" className="mono">{p.toFixed(2)}</text>;
        })}

        {/* Volume background overlay (when "Show Volume Overlay" is on) */}
        {settings.volumeOverlay && (() => {
          const maxVol = Math.max(...display.map((d) => d.volume), 1);
          return display.map((d, i) => {
            const x = padL + i * cwRaw + (cwRaw - cwBody) / 2;
            const h = (d.volume / maxVol) * (pricePaneH * 0.35);
            return <rect key={`bgv-${i}`} x={x} y={pricePaneH - h} width={cwBody} height={h}
                         fill={d.close >= d.open ? '#1fb37e' : '#d84f4f'} opacity=".15" />;
          });
        })()}

        {/* Trade-price tag */}
        {settings.tradePrice && (
          <g>
            <rect x={W - padR - 2} y={yPrice(price) - 8} width={padR + 2} height={16} fill={pctChange >= 0 ? '#1fb37e' : '#d84f4f'} />
            <text x={W - padR / 2} y={yPrice(price) + 4} textAnchor="middle" fontSize="10" fontWeight="700" fill="#0a1120">{price.toFixed(2)}</text>
            <line x1={padL} x2={W - padR} y1={yPrice(price)} y2={yPrice(price)} stroke={pctChange >= 0 ? '#1fb37e' : '#d84f4f'} strokeDasharray="2 3" strokeWidth=".7" opacity=".6" />
          </g>
        )}

        {/* Chart H/L lines */}
        {settings.chartHighLow && (
          <g>
            <line x1={padL} x2={W - padR} y1={yPrice(winHigh)} y2={yPrice(winHigh)} stroke="#3ddc84" strokeDasharray="3 3" strokeWidth=".9" />
            <text x={padL + 4} y={yPrice(winHigh) - 3} fontSize="9" fill="#3ddc84">H {winHigh.toFixed(2)}</text>
            <line x1={padL} x2={W - padR} y1={yPrice(winLow)} y2={yPrice(winLow)} stroke="#ff6b6b" strokeDasharray="3 3" strokeWidth=".9" />
            <text x={padL + 4} y={yPrice(winLow) + 12} fontSize="9" fill="#ff6b6b">L {winLow.toFixed(2)}</text>
          </g>
        )}

        {/* Position avg-price line */}
        {settings.positions && positionAvgPrice != null && (
          <g>
            <line x1={padL} x2={W - padR} y1={yPrice(positionAvgPrice)} y2={yPrice(positionAvgPrice)}
                  stroke="#3dd6ff" strokeWidth="1.2" strokeDasharray="6 3" />
            <rect x={W - padR - 70} y={yPrice(positionAvgPrice) - 8} width={66} height={16} fill="#3dd6ff" />
            <text x={W - padR - 4} y={yPrice(positionAvgPrice) + 4} textAnchor="end" fontSize="10" fontWeight="700" fill="#0a1120">
              POS {positionQuantity ?? ''} @ {positionAvgPrice.toFixed(2)}
            </text>
          </g>
        )}

        {/* price overlays (studies) */}
        {activePrice.map((s) => {
          const r = s.compute(display);
          return (
            <g key={s.id}>
              {r.lines.map((line) => (
                <path key={line.name}
                      d={pathFor(line.values, (i, v) => `${xAt(i)},${yPrice(v)}`)}
                      fill="none" stroke={line.color} strokeDasharray={line.dashed ? '2 3' : undefined}
                      strokeWidth={line.thickness ?? 1.1} />
              ))}
            </g>
          );
        })}

        {/* main series */}
        {chartType === 'line' && (
          <path d={pathFor(display.map(d => d.close), (i, v) => `${xAt(i)},${yPrice(v)}`)} fill="none" stroke="#3dd6ff" strokeWidth="1.4" />
        )}
        {chartType === 'area' && (
          <>
            <defs><linearGradient id={`areaGrad-${symbol}`} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#3dd6ff" stopOpacity=".55" />
              <stop offset="100%" stopColor="#3dd6ff" stopOpacity="0" />
            </linearGradient></defs>
            <path d={`${pathFor(display.map(d => d.close), (i, v) => `${xAt(i)},${yPrice(v)}`)} L${xAt(display.length - 1)},${pricePaneH} L${xAt(0)},${pricePaneH} Z`} fill={`url(#areaGrad-${symbol})`} />
            <path d={pathFor(display.map(d => d.close), (i, v) => `${xAt(i)},${yPrice(v)}`)} fill="none" stroke="#3dd6ff" strokeWidth="1.4" />
          </>
        )}
        {(chartType === 'candle' || chartType === 'hollow' || chartType === 'heikin' || chartType === 'bar') &&
          display.map((d, i) => {
            const x = padL + i * cwRaw + (cwRaw - cwBody) / 2;
            const yo = yPrice(d.open), yc = yPrice(d.close), yh = yPrice(d.high), yl = yPrice(d.low);
            const up = d.close >= d.open;
            const stroke = up ? '#3ddc84' : '#ff6b6b';
            const fillSolid = up ? '#1fb37e' : '#d84f4f';
            const top = Math.min(yo, yc); const bodyH = Math.max(1, Math.abs(yo - yc));
            if (chartType === 'bar') {
              return (
                <g key={i} stroke={stroke} strokeWidth="1.1">
                  <line x1={x + cwBody / 2} x2={x + cwBody / 2} y1={yh} y2={yl} />
                  <line x1={x} x2={x + cwBody / 2} y1={yo} y2={yo} />
                  <line x1={x + cwBody / 2} x2={x + cwBody} y1={yc} y2={yc} />
                </g>
              );
            }
            const fill = chartType === 'hollow' ? (up ? 'transparent' : fillSolid) : fillSolid;
            return (
              <g key={i} stroke={stroke} fill={fill} strokeWidth=".7">
                <line x1={x + cwBody / 2} x2={x + cwBody / 2} y1={yh} y2={yl} />
                <rect x={x} y={top} width={cwBody} height={bodyH} />
              </g>
            );
          })}

        {/* Working-order overlays */}
        {showOrders.filter((o) => o.limitPrice).map((o) => {
          const p = Number(o.limitPrice) / 100; const y = yPrice(p);
          return (
            <g key={o.brokerOrderId}>
              <line x1={padL} x2={W - padR} y1={y} y2={y} stroke={o.side === 'BUY' ? '#3ddc84' : '#ff6b6b'} strokeDasharray="4 3" strokeWidth=".9" opacity=".7" />
              <rect x={W - padR - 30} y={y - 7} width={28} height={14} fill={o.side === 'BUY' ? '#3ddc84' : '#ff6b6b'} />
              <text x={W - padR - 16} y={y + 3} textAnchor="middle" fontSize="9" fontWeight="700" fill="#0a1120">{o.side}</text>
            </g>
          );
        })}

        {/* Alerts */}
        {settings.alerts && (alerts ?? []).map((a, i) => {
          const y = yPrice(a.price);
          return (
            <g key={`alert-${i}`}>
              <line x1={padL} x2={W - padR} y1={y} y2={y} stroke="#fbbf24" strokeWidth=".8" strokeDasharray="1 4" />
              <polygon points={`${padL + 2},${y} ${padL + 12},${y - 6} ${padL + 12},${y + 6}`} fill="#fbbf24" />
              <text x={padL + 16} y={y + 3} fontSize="9" fontWeight="700" fill="#fbbf24">
                Alert {a.direction === 'above' ? '↑' : '↓'} {a.price.toFixed(2)} {a.label ? `· ${a.label}` : ''}
              </text>
            </g>
          );
        })}

        {/* Earnings & Dividends event icons */}
        {settings.earningsIcon && (earningsDates ?? []).map((t, i) => {
          const idx = display.findIndex((d) => d.t >= t);
          if (idx < 0) return null;
          const x = xAt(idx);
          return (
            <g key={`earn-${i}`}>
              <circle cx={x} cy={pricePaneH - 6} r={7} fill="#3dd6ff" />
              <text x={x} y={pricePaneH - 3} textAnchor="middle" fontSize="9" fontWeight="800" fill="#0a1120">E</text>
              {settings.earningsPreview && (
                <rect x={x - 18} y={pricePaneH - 36} width={36} height={20} fill="#0e1828" stroke="#3dd6ff" />
              )}
              {settings.earningsPreview && (
                <text x={x} y={pricePaneH - 22} textAnchor="middle" fontSize="9" fill="#3dd6ff">EPS prev</text>
              )}
            </g>
          );
        })}
        {settings.dividendsIcon && (dividendsDates ?? []).map((t, i) => {
          const idx = display.findIndex((d) => d.t >= t);
          if (idx < 0) return null;
          const x = xAt(idx);
          return (
            <g key={`div-${i}`}>
              <circle cx={x} cy={pricePaneH - 22} r={7} fill="#3ddc84" />
              <text x={x} y={pricePaneH - 19} textAnchor="middle" fontSize="9" fontWeight="800" fill="#06231a">D</text>
            </g>
          );
        })}

        {/* Inlined zero-centred oscillators (Fit Studies in Y-axis) */}
        {inlineSubData.map((p, i) => {
          // Map each oscillator into a thin band at the bottom of the price pane.
          const bandH = 28;
          const bandTop = pricePaneH - 6 - (i + 1) * (bandH + 4);
          const finite = p.result.lines.flatMap((l) => l.values).filter(Number.isFinite) as number[];
          const mn = Math.min(...finite, 0), mx = Math.max(...finite, 0);
          const span2 = (mx - mn) || 1;
          const yScale = (v: number) => bandTop + bandH - ((v - mn) / span2) * bandH;
          return (
            <g key={`inline-${p.def.id}`} opacity=".75">
              <text x={padL + 4} y={bandTop + 8} fontSize="9" fill="#7a8cab">{p.def.short}</text>
              {p.result.lines.map((l) => (
                <path key={l.name} d={pathFor(l.values.map((v) => Number.isFinite(v) ? v : (mn + mx) / 2),
                                              (k, v) => `${xAt(k)},${yScale(v)}`)}
                      fill="none" stroke={l.color} strokeWidth=".9" />
              ))}
            </g>
          );
        })}

        {/* Option positions (NGX has no options market — show a hint when toggled). */}
        {settings.optionPositions && (
          <text x={padL + 4} y={padT + 14} fontSize="9" fill="#fbbf24">
            Option Positions: NGX does not list listed options yet.
          </text>
        )}

        {/* x-axis date labels */}
        {axisDateTicks(display, timeframe).map(({ i, label }) => (
          <text key={i} x={xAt(i)} y={pricePaneH + 12} fontSize="9" fill="#7a8cab" textAnchor="middle">{label}</text>
        ))}

        {/* drawings */}
        {drawings.map((d) => renderDrawing(d, { padL, padR, W, yPrice, domainToSvg, deleteDrawing }))}
        {pendingPts.length > 0 && pendingPts.map((p, i) => {
          const s = domainToSvg(p);
          return <circle key={i} cx={s.x} cy={s.y} r={3} fill="#fbbf24" stroke="#0a1120" />;
        })}

        {/* sub panes */}
        {subPanesData.map((p, idx) => {
          const top = pricePaneH + idx * subPaneH;
          return <SubPane key={p.def.id} top={top} h={subPaneH} W={W} padL={padL} padR={padR} xAt={xAt}
                          def={p.def} result={p.result} display={display} />;
        })}

        {/* crosshair */}
        {settings.crosshair && hover && (
          <g pointerEvents="none">
            <line x1={xAt(hover.i)} x2={xAt(hover.i)} y1={padT} y2={pricePaneH} stroke="#3dd6ff" strokeDasharray="2 2" strokeWidth=".5" opacity=".6" />
            <line x1={padL} x2={W - padR} y1={hover.y} y2={hover.y} stroke="#3dd6ff" strokeDasharray="2 2" strokeWidth=".5" opacity=".6" />
            <rect x={hover.x + 8} y={hover.y - 32} width={120} height={48} fill="#0a1120" stroke="#1f2a44" />
            {(() => {
              const d = display[hover.i]; if (!d) return null;
              return (<>
                <text x={hover.x + 14} y={hover.y - 18} fontSize="10" fill="#dfe6f2">O {d.open.toFixed(2)} H {d.high.toFixed(2)}</text>
                <text x={hover.x + 14} y={hover.y - 6}  fontSize="10" fill="#dfe6f2">L {d.low.toFixed(2)} C {d.close.toFixed(2)}</text>
                <text x={hover.x + 14} y={hover.y + 8}  fontSize="10" fill="#7a8cab">{new Date(d.t).toISOString().slice(0,10)}</text>
              </>);
            })()}
          </g>
        )}
      </svg>

      <div className="ch-strip">
        {(workingOrders ?? []).map((o) => (
          <div key={o.brokerOrderId} className="ch-strip-row">
            <span className="ch-strip-time">{(o.placedAt ?? '').slice(11, 19) || '—'}</span>
            <span className={`ch-strip-side ${o.side === 'SELL' ? 'down' : 'up'}`}>{o.side}</span>
            <span className="ch-strip-type">{o.type}</span>
            <span className="ch-strip-qty">{o.quantity}</span>
            <span className="ch-strip-px">{o.limitPrice ? `₦${(Number(o.limitPrice) / 100).toFixed(2)}` : '—'}</span>
            <span className="ch-strip-status up">{o.status}</span>
            <button className="ch-strip-cancel" onClick={() => void cancelOrder(o)}>Cancel</button>
          </div>
        ))}
        {(!workingOrders || workingOrders.length === 0) && (
          <div className="ch-strip-row empty">No working orders for {symbol}.</div>
        )}
      </div>
    </div>
  );
}

// ===== drawing renderer =====
interface DrawCtx {
  padL: number; padR: number; W: number;
  yPrice: (p: number) => number;
  domainToSvg: (p: Point) => { x: number; y: number };
  deleteDrawing: (id: string) => void;
}
function renderDrawing(d: Drawing, ctx: DrawCtx): React.ReactNode {
  const stroke = d.color ?? '#fbbf24';
  if (d.kind === 'price' && d.pts[0]) {
    const y = ctx.yPrice(d.pts[0].y);
    return (<g key={d.id} onDoubleClick={() => ctx.deleteDrawing(d.id)} style={{ cursor: 'pointer' }}>
      <line x1={ctx.padL} x2={ctx.W - ctx.padR} y1={y} y2={y} stroke={stroke} strokeWidth="1" />
      <text x={ctx.padL + 4} y={y - 3} fontSize="10" fill={stroke}>{d.pts[0].y.toFixed(2)}</text>
    </g>);
  }
  if (d.kind === 'time' && d.pts[0]) {
    const x = ctx.domainToSvg(d.pts[0]).x;
    return (<g key={d.id} onDoubleClick={() => ctx.deleteDrawing(d.id)} style={{ cursor: 'pointer' }}>
      <line x1={x} x2={x} y1={0} y2={ctx.yPrice(0) || 1000} stroke={stroke} strokeWidth="1" strokeDasharray="3 3" />
    </g>);
  }
  if (d.kind === 'trendline' && d.pts.length === 2) {
    const a = ctx.domainToSvg(d.pts[0]!), b = ctx.domainToSvg(d.pts[1]!);
    return <line key={d.id} x1={a.x} x2={b.x} y1={a.y} y2={b.y} stroke={stroke} strokeWidth="1.4"
                 onDoubleClick={() => ctx.deleteDrawing(d.id)} style={{ cursor: 'pointer' }} />;
  }
  if (d.kind === 'rect' && d.pts.length === 2) {
    const a = ctx.domainToSvg(d.pts[0]!), b = ctx.domainToSvg(d.pts[1]!);
    const x = Math.min(a.x, b.x), y = Math.min(a.y, b.y);
    const w = Math.abs(a.x - b.x), h = Math.abs(a.y - b.y);
    return <rect key={d.id} x={x} y={y} width={w} height={h} fill="rgba(251,191,36,.08)" stroke={stroke} strokeWidth="1"
                 onDoubleClick={() => ctx.deleteDrawing(d.id)} style={{ cursor: 'pointer' }} />;
  }
  if (d.kind === 'text' && d.pts[0]) {
    const s = ctx.domainToSvg(d.pts[0]);
    return (<g key={d.id} onDoubleClick={() => ctx.deleteDrawing(d.id)} style={{ cursor: 'pointer' }}>
      <rect x={s.x - 2} y={s.y - 12} width={(d.label ?? '').length * 6 + 6} height={16} fill="rgba(10, 17, 32, .85)" stroke={stroke} />
      <text x={s.x + 2} y={s.y} fontSize="11" fill={stroke}>{d.label ?? ''}</text>
    </g>);
  }
  if (d.kind === 'fibRetrace' && d.pts.length === 2) {
    const high = Math.max(d.pts[0]!.y, d.pts[1]!.y);
    const low = Math.min(d.pts[0]!.y, d.pts[1]!.y);
    const xa = ctx.domainToSvg(d.pts[0]!).x; const xb = ctx.domainToSvg(d.pts[1]!).x;
    const x1 = Math.min(xa, xb), x2 = Math.max(xa, xb);
    const levels = fibRetracePrices(high, low);
    return (<g key={d.id} onDoubleClick={() => ctx.deleteDrawing(d.id)} style={{ cursor: 'pointer' }}>
      {levels.map((lv, i) => {
        const y = ctx.yPrice(lv.price);
        return (<g key={i}>
          <line x1={x1} x2={ctx.W - ctx.padR} y1={y} y2={y} stroke={lv.color} strokeWidth=".9" strokeDasharray="2 3" />
          <text x={x1 - 4} y={y - 2} fontSize="9" fill={lv.color} textAnchor="end">{lv.label} · {lv.price.toFixed(2)}</text>
        </g>);
      })}
      <line x1={x1} x2={x2} y1={ctx.yPrice(high)} y2={ctx.yPrice(low)} stroke="#fbbf24" strokeDasharray="2 2" />
    </g>);
  }
  if (d.kind === 'fibExtend' && d.pts.length === 3) {
    const a = d.pts[0]!.y, b = d.pts[1]!.y, c = d.pts[2]!.y;
    const xc = ctx.domainToSvg(d.pts[2]!).x;
    const levels = fibExtendPrices(a, b, c);
    return (<g key={d.id} onDoubleClick={() => ctx.deleteDrawing(d.id)} style={{ cursor: 'pointer' }}>
      {levels.map((lv, i) => {
        const y = ctx.yPrice(lv.price);
        return (<g key={i}>
          <line x1={xc} x2={ctx.W - ctx.padR} y1={y} y2={y} stroke={lv.color} strokeWidth=".9" strokeDasharray="2 3" />
          <text x={xc - 4} y={y - 2} fontSize="9" fill={lv.color} textAnchor="end">{lv.label} · {lv.price.toFixed(2)}</text>
        </g>);
      })}
    </g>);
  }
  if (d.kind === 'channel' && d.pts.length === 3) {
    const c = channelLines(d.pts[0]!, d.pts[1]!, d.pts[2]!);
    const ba = ctx.domainToSvg(c.base[0]), bb = ctx.domainToSvg(c.base[1]);
    const pa = ctx.domainToSvg(c.parallel[0]), pb = ctx.domainToSvg(c.parallel[1]);
    return (<g key={d.id} onDoubleClick={() => ctx.deleteDrawing(d.id)} style={{ cursor: 'pointer' }}>
      <line x1={ba.x} x2={bb.x} y1={ba.y} y2={bb.y} stroke="#3dd6ff" strokeWidth="1.2" />
      <line x1={pa.x} x2={pb.x} y1={pa.y} y2={pb.y} stroke="#3dd6ff" strokeWidth="1.2" strokeDasharray="3 3" />
      <polygon points={`${ba.x},${ba.y} ${bb.x},${bb.y} ${pb.x},${pb.y} ${pa.x},${pa.y}`} fill="rgba(61,214,255,.06)" />
    </g>);
  }
  return null;
}

function SubPane({ top, h, W, padL, padR, xAt, def, result, display }: {
  top: number; h: number; W: number; padL: number; padR: number;
  xAt: (i: number) => number; def: { short: string }; result: StudyResult; display: Candle[];
}) {
  const finiteVals: number[] = [];
  result.lines.forEach((l) => l.values.forEach((v) => Number.isFinite(v) && finiteVals.push(v)));
  if (result.histogram) result.histogram.values.forEach((v) => Number.isFinite(v) && finiteVals.push(v));
  let mn = result.domain ? result.domain[0] : (finiteVals.length ? Math.min(...finiteVals) : 0);
  let mx = result.domain ? result.domain[1] : (finiteVals.length ? Math.max(...finiteVals) : 1);
  if (mn === mx) mx = mn + 1;
  const padInner = 8;
  const yScale = (v: number) => top + padInner + (mx - v) / (mx - mn) * (h - padInner * 2);
  const zeroY = result.zeroLine ? yScale(0) : null;
  return (
    <g>
      <rect x={0} y={top} width={W} height={h} fill="#0a0e18" />
      <line x1={0} x2={W} y1={top} y2={top} stroke="#1f2a44" />
      <text x={padL + 4} y={top + 12} fontSize="10" fill="#7a8cab">{def.short}</text>
      {zeroY != null && <line x1={padL} x2={W - padR} y1={zeroY} y2={zeroY} stroke="#3a4a6e" strokeDasharray="2 3" strokeWidth=".5" />}
      {result.histogram && (() => {
        const baseY = result.zeroLine ? yScale(0) : yScale(mn);
        return result.histogram!.values.map((v, i) => {
          if (!Number.isFinite(v)) return null;
          const y = yScale(v); const yTop = Math.min(baseY, y); const yH = Math.max(1, Math.abs(baseY - y));
          const up = v >= 0; const cw = (W - padL - padR) / display.length;
          const x = padL + i * cw + cw * 0.15;
          return <rect key={i} x={x} y={yTop} width={Math.max(1, cw * 0.7)} height={yH} fill={up ? '#1fb37e' : '#d84f4f'} opacity=".75" />;
        });
      })()}
      {result.lines.map((l) => (
        <path key={l.name}
              d={pathFor(l.values.map((v) => Number.isFinite(v) ? v : (mx + mn) / 2), (i, v) => `${xAt(i)},${yScale(v)}`)}
              fill="none" stroke={l.color} strokeDasharray={l.dashed ? '2 3' : undefined} strokeWidth={l.thickness ?? 1.1} />
      ))}
      <text x={W - padR + 6} y={yScale(mx) + 4} fontSize="9" fill="#7a8cab">{mx.toFixed(0)}</text>
      <text x={W - padR + 6} y={yScale(mn) + 4} fontSize="9" fill="#7a8cab">{mn.toFixed(0)}</text>
    </g>
  );
}

function Pop({ label, open, onToggle, children }: { label: string; open: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (<div className="ch-pop-wrap">
    <button className={`ch-btn ghost ${open ? 'open' : ''}`} onClick={onToggle}>{label}</button>
    {open && <div className="ch-popover wide">{children}</div>}
  </div>);
}

function StudiesPanel({ active, onToggle }: { active: Set<StudyId>; onToggle: (id: StudyId) => void }) {
  const activeList = STUDY_REGISTRY.filter((s) => active.has(s.id));
  return (
    <div className="studies-list">
      <div className="studies-section-h">Active</div>
      <div className="studies-chips">
        {activeList.length === 0 && <span className="studies-empty">No studies active.</span>}
        {activeList.map((s) => (
          <button key={s.id} className="study-chip on" onClick={() => onToggle(s.id)}>
            {s.label} <span className="x">×</span>
          </button>
        ))}
      </div>
      {STUDY_CATEGORIES.map((cat) => {
        const items = STUDY_REGISTRY.filter((s) => s.category === cat);
        return (<div key={cat}>
          <div className="studies-section-h">{cat}</div>
          <div className="studies-chips">
            {items.map((s) => (
              <button key={s.id} className={`study-chip ${active.has(s.id) ? 'on' : ''}`} onClick={() => onToggle(s.id)}>{s.label}</button>
            ))}
          </div>
        </div>);
      })}
    </div>
  );
}

function DrawingsPanel({ active, onPick, onClear, onClose, setName, allSets, onPickSet, onNewSet, count }: {
  active?: DrawingKind; onPick: (t: DrawingKind) => void;
  onClear: () => void; onClose: () => void;
  setName: string; allSets: string[];
  onPickSet: (n: string) => void; onNewSet: () => void;
  count: number;
}) {
  return (
    <div className="drawings-list">
      <div className="drawings-head">
        <strong>Drawings</strong>
        <button className="ch-tool-cancel" onClick={onClose} aria-label="Close">×</button>
      </div>
      <div className="drawings-section-h">Drawing Set</div>
      <div className="drawings-set-row">
        <select value={setName} onChange={(e) => onPickSet(e.target.value)}>
          {allSets.map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
        <button className="drawings-new" onClick={onNewSet}>+ New Drawing Set</button>
      </div>
      <div className="drawings-section-h">Drawing Tools</div>
      <div className="drawings-tools">
        {TOOL_ORDER.map((k) => (
          <button key={k} className={`drawing-tool ${active === k ? 'on' : ''}`} onClick={() => onPick(k)}>
            <span className="glyph">{TOOL_GLYPHS[k]}</span>
            <span className="label">{TOOL_LABELS[k]}</span>
          </button>
        ))}
      </div>
      <div className="drawings-footer">
        <span className="muted">{count} drawing{count === 1 ? '' : 's'} on chart · double-click any to delete</span>
        <button className="ch-popover-item danger" onClick={onClear}>Clear all</button>
      </div>
    </div>
  );
}

// ===== SETTINGS PANEL — matches the Chart Settings screenshot =====
function SettingsPanel({ value, onChange, onClose }: { value: ChartSettings; onChange: (s: ChartSettings) => void; onClose: () => void }) {
  const set = <K extends keyof ChartSettings>(k: K, v: ChartSettings[K]) => onChange({ ...value, [k]: v });
  const Row = ({ k, label, sub }: { k: keyof ChartSettings; label: string; sub?: boolean }) => (
    <label className={`cs-row ${sub ? 'sub' : ''}`}>
      <input type="checkbox" checked={Boolean(value[k])} onChange={(e) => set(k, e.target.checked as any)} />
      <span>{label}</span>
    </label>
  );
  return (
    <div className="ch-popover wide cs-popover">
      <div className="cs-head">
        <strong>Chart Settings</strong>
        <button className="cs-close" onClick={onClose}>Close</button>
      </div>
      <Row k="extendedHours"   label="Show Extended Hours" />
      <Row k="alerts"          label="Show Alerts" />
      <Row k="optionPositions" label="Show Option Positions" />
      <Row k="workingOrders"   label="Show Working Orders" />
      <Row k="tradePrice"      label="Show Trade Price" />
      <Row k="chartHighLow"    label="Chart High/Low" />
      <Row k="earningsIcon"    label="Show Earnings Icon" />
      <Row k="earningsPreview" label="Show Earnings Preview" sub />
      <Row k="dividendsIcon"   label="Show Dividends Icon" />
      <Row k="fitStudies"      label="Fit Studies in Y-axis" />
      <Row k="positions"       label="Show Positions" />
      <Row k="volumeOverlay"   label="Show Volume Overlay" />
      <Row k="crosshair"       label="Show Crosshair" />
      <Row k="zoomOnScroll"    label="Zoom on scroll" />
      <hr className="cs-divider" />
      <Row k="gridlines"       label="Gridlines" />
      <Row k="logScale"        label="Logarithmic Y-axis" />
      <div className="cs-row">
        <span>Candle width</span>
        <input type="range" min={0.5} max={1.5} step={0.1}
               value={value.candleWidthScale}
               onChange={(e) => set('candleWidthScale', Number(e.target.value))}
               style={{ flex: 1, marginLeft: 8 }} />
      </div>
    </div>
  );
}

// ===== helpers =====
function pathFor(values: number[], to: (i: number, v: number) => string): string {
  const out: string[] = []; let started = false;
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (v == null || !Number.isFinite(v)) { started = false; continue; }
    out.push(`${started ? 'L' : 'M'}${to(i, v)}`); started = true;
  }
  return out.join(' ');
}
function toHeikinAshi(xs: Candle[]): Candle[] {
  if (xs.length === 0) return xs;
  const out: Candle[] = []; let prevOpen = xs[0]!.open; let prevClose = xs[0]!.close;
  for (const d of xs) {
    const close = (d.open + d.high + d.low + d.close) / 4;
    const open = (prevOpen + prevClose) / 2;
    const high = Math.max(d.high, open, close); const low = Math.min(d.low, open, close);
    out.push({ ...d, open, high, low, close }); prevOpen = open; prevClose = close;
  }
  return out;
}
function labelOfType(t: ChartType): string {
  return ({ candle: 'Candlestick', hollow: 'Hollow Candle', bar: 'OHLC Bar', line: 'Line', area: 'Area', heikin: 'Heikin-Ashi' } as Record<ChartType, string>)[t];
}
function axisDateTicks(data: Candle[], tf: Timeframe): Array<{ i: number; label: string }> {
  if (data.length === 0) return [];
  // Aim for ~8-12 labels depending on the timeframe so they always feel
  // well-spaced and never crowd each other.
  const targetPer = tf === '1m' || tf === '5m' ? 14
                  : tf === '15m' || tf === '30m' ? 12
                  : tf === '1H' || tf === '4H' ? 10
                  : tf === 'D' ? 10
                  : tf === 'W' ? 9
                  : 8;
  const out: Array<{ i: number; label: string }> = [];
  const step = Math.max(1, Math.floor(data.length / targetPer));
  for (let i = 0; i < data.length; i += step) {
    const d = new Date(data[i]!.t);
    out.push({ i, label: formatTickLabel(d, tf) });
  }
  // Ensure the last bar is always labelled so users see the latest date/time.
  const lastI = data.length - 1;
  if (out.length > 0 && out[out.length - 1]!.i !== lastI) {
    out.push({ i: lastI, label: formatTickLabel(new Date(data[lastI]!.t), tf) });
  }
  return out;
}
function formatTickLabel(d: Date, tf: Timeframe): string {
  const mon = d.toLocaleString('en-NG', { month: 'short' });
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  const dd = d.getUTCDate();
  const yy = d.getUTCFullYear() % 100;
  // Minutes — HH:MM only (intraday)
  if (tf === '1m' || tf === '5m' || tf === '15m' || tf === '30m') return hh + ':' + mm;
  // Hours — include date for a cleaner multi-session read
  if (tf === '1H' || tf === '4H') return dd + ' ' + mon + ' ' + hh + ':' + mm;
  // Days — DD Mon
  if (tf === 'D') return dd + ' ' + mon;
  // Weeks — Wn Mon
  if (tf === 'W') return 'W' + Math.ceil(dd / 7) + ' ' + mon;
  // Months — Mon 'YY
  return mon + ' \'' + String(yy).padStart(2, '0');
}
function generateCandles(symbol: string, tf: Timeframe, n: number): Candle[] {
  // Deterministic per (symbol, timeframe) but with a Mulberry32 PRNG so
  // distribution is actually uniform. A simple LCG with period 233_280 was
  // producing a biased sign on rand()-0.5, which in turn gave a monotonic
  // staircase (all red, all down) — see the support ticket with the
  // ZENITHBANK chart. Mulberry32 fixes that and the mean-reversion/trend
  // terms below keep the series bounded inside a realistic band.
  let h = 2166136261 >>> 0;
  for (let i = 0; i < symbol.length; i++) h = Math.imul(h ^ symbol.charCodeAt(i), 16777619) >>> 0;
  for (let i = 0; i < tf.length; i++) h = Math.imul(h ^ tf.charCodeAt(i), 16777619) >>> 0;
  let state = h >>> 0;
  const rand = () => {
    state = (state + 0x6D2B79F5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  // Gaussian-ish via average of two uniforms, so big moves are rare.
  const rnorm = () => (rand() + rand() - 1);

  const anchor = 50 + (h % 8000) / 100; // 50.00 .. 130.00, stable per symbol
  const drift = (((h >>> 4) % 7) - 3) / 10000; // -0.0003 .. +0.0003 per bar
  const vol = 0.008 + ((h >>> 8) % 20) / 10000; // 0.8% .. 1.0% stepwise volatility

  const out: Candle[] = [];
  let last = anchor;
  const stepMs = tfToMs(tf);
  const start = Date.now() - n * stepMs;
  for (let i = 0; i < n; i++) {
    // Mean reversion toward anchor + small trend + stochastic shock.
    const revert = (anchor - last) * 0.05;
    const shock = rnorm() * last * vol;
    const open = last;
    const close = Math.max(0.5, open + revert + drift * last + shock);
    // Realistic wicks: up to ~40% of body size beyond the body ends.
    const body = Math.abs(close - open);
    const wickUp = rand() * Math.max(body * 0.6, last * 0.003);
    const wickDn = rand() * Math.max(body * 0.6, last * 0.003);
    const high = Math.max(open, close) + wickUp;
    const low = Math.max(0.25, Math.min(open, close) - wickDn);
    // Volume correlates weakly with absolute return so green/red candles
    // both get a range of sizes.
    const volume = Math.floor(80_000 + Math.abs(shock) / last * 12_000_000 + rand() * 120_000);
    out.push({ t: start + i * stepMs, open, close, high, low, volume });
    last = close;
  }
  return out;
}
function tfToMs(tf: Timeframe): number {
  switch (tf) {
    case '1m':  return 60_000; case '5m':  return 5*60_000; case '15m': return 15*60_000;
    case '30m': return 30*60_000; case '1H':  return 60*60_000; case '4H':  return 4*60*60_000;
    case 'D':   return 24*60*60_000; case 'W':   return 7*24*60*60_000; case 'M':   return 30*24*60*60_000;
  }
}

/**
 * QuickTradeBar — chart-embedded bid/ask quote strip with Sell (red) and Buy
 * (green) buttons. Click either to jump to the Trade view pre-filled with the
 * chosen side, symbol, and price. The parent is responsible for what "jump"
 * means; by default this is wired into Terminal.onQuickTrade.
 *
 * When bid/ask/sizes are not supplied we seed them deterministically from the
 * displayed price so every cell has a realistic-looking spread and depth even
 * in the offline demo. In production these values flow from the Level-II
 * stream on the WebSocket.
 */
function QuickTradeBar({ symbol, price, pctChange, bid, ask, bidSize, askSize, onQuickTrade }: {
  symbol: string; price: number; pctChange: number;
  bid?: number; ask?: number; bidSize?: number; askSize?: number;
  onQuickTrade?: (side: 'BUY' | 'SELL', symbol: string, price: number) => void;
}) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < symbol.length; i++) h = Math.imul(h ^ symbol.charCodeAt(i), 16777619) >>> 0;
  const seededSpreadBps = 8 + (h % 20);
  const halfSpread = price * (seededSpreadBps / 2) / 10000;
  const bidPx = bid ?? Math.max(0.01, price - halfSpread);
  const askPx = ask ?? (price + halfSpread);
  const bidSz = bidSize ?? ((h >>> 3) % 900) + 100;
  const askSz = askSize ?? ((h >>> 9) % 900) + 100;

  const changeAbs = price * (pctChange / 100);
  const pctTone = pctChange >= 0 ? 'up' : 'down';

  return (
    <div className="ch-qt" role="region" aria-label={`${symbol} quick trade`}>
      <div className="ch-qt-side ch-qt-bid">
        <div className="ch-qt-px mono">{bidPx.toFixed(2)}</div>
        <div className="ch-qt-sz">Bid size: {bidSz.toLocaleString('en-NG')}</div>
      </div>

      <button
        type="button"
        className="ch-qt-btn ch-qt-sell"
        aria-label={`Sell ${symbol} at ${bidPx.toFixed(2)}`}
        onClick={(e) => { e.stopPropagation(); onQuickTrade?.('SELL', symbol, bidPx); }}
      >Sell</button>

      <div className="ch-qt-mid">
        <div className="ch-qt-last mono">{price.toFixed(2)}</div>
        <div className={`ch-qt-chg ${pctTone}`}>
          {(changeAbs >= 0 ? '+' : '') + changeAbs.toFixed(2)} ({(pctChange >= 0 ? '+' : '') + pctChange.toFixed(2)}%)
        </div>
      </div>

      <button
        type="button"
        className="ch-qt-btn ch-qt-buy"
        aria-label={`Buy ${symbol} at ${askPx.toFixed(2)}`}
        onClick={(e) => { e.stopPropagation(); onQuickTrade?.('BUY', symbol, askPx); }}
      >Buy</button>

      <div className="ch-qt-side ch-qt-ask">
        <div className="ch-qt-px mono">{askPx.toFixed(2)}</div>
        <div className="ch-qt-sz">Ask size: {askSz.toLocaleString('en-NG')}</div>
      </div>
    </div>
  );
}


/**
 * TrafficLights — three circles (green/amber/red) showing BUY/HOLD/SELL.
 * Recomputes on every re-render so it reflects any live price/tick change.
 * Hover surfaces the composite score and the top drivers.
 */
function TrafficLights(p: { symbol: string; price: number; pctChange: number; bid?: number; ask?: number; recentCloses?: number[] }) {
  const sig = scoreStock({ symbol: p.symbol, price: p.price, pctChange: p.pctChange, bid: p.bid, ask: p.ask, recentCloses: p.recentCloses });
  const { verdict, score, technical, fundamental, drivers } = sig;
  const title =
    verdict + ' (' + score + ')\n' +
    'Technical: ' + technical + ' / Fundamental: ' + fundamental +
    (drivers.length ? '\n\nTop drivers:\n- ' + drivers.join('\n- ') : '');
  const cls = (v: Verdict) => 'tl-dot ' + (verdict === v ? 'on tl-' + v.toLowerCase() : 'off');
  return (
    <span className="tl" role="img" aria-label={verdict + ' (score ' + score + ')'} title={title}>
      <span className={cls('BUY')} aria-hidden="true" />
      <span className={cls('HOLD')} aria-hidden="true" />
      <span className={cls('SELL')} aria-hidden="true" />
      <span className="tl-label">{verdict}</span>
    </span>
  );
}
